import React, { useState } from "react";
import { BrandMark, Ico } from "./primitives.jsx";
import { apiPost, ApiError, navigate } from "./api.jsx";

function getRecaptchaSiteKey() {
  const el = document.querySelector('meta[name="recaptcha-site-key"]');
  return el ? el.getAttribute("content") : "";
}

// reCAPTCHA v3 is invisible: the <script> tag in spa.html loads and exposes
// window.grecaptcha; we call .execute(siteKey, {action}) to get a short-lived
// token which the server verifies against Google's API. If the key is absent
// (reCAPTCHA disabled), we send "" and the server skips verification.
async function getRecaptchaToken(action) {
  const siteKey = getRecaptchaSiteKey();
  if (!siteKey) return "";
  if (!window.grecaptcha || !window.grecaptcha.execute) {
    // Wait up to ~5s for the async script to finish loading.
    const deadline = Date.now() + 5000;
    while (Date.now() < deadline && (!window.grecaptcha || !window.grecaptcha.execute)) {
      await new Promise(r => setTimeout(r, 100));
    }
    if (!window.grecaptcha || !window.grecaptcha.execute) {
      throw new Error("reCAPTCHA could not load. Please refresh the page and try again.");
    }
  }
  await new Promise(resolve => window.grecaptcha.ready(resolve));
  return await window.grecaptcha.execute(siteKey, { action });
}

export function Auth({ mode = "login", setMode }) {
  const isLogin = mode === "login";
  const [form, setForm] = useState({
    user_name: "",
    company_name: "",
    user_title: "",
    account_type: "company",
    email: "",
    password: "",
    password_confirm: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      if (isLogin) {
        const recaptcha_token = await getRecaptchaToken("login");
        await apiPost("/auth/login/", {
          email: form.email,
          password: form.password,
          recaptcha_token,
        });
        // On success, move to the dashboard. Hard-reload so the SPA
        // re-reads /me/ with the freshly-established session.
        navigate("dashboard", { hard: true });
      } else {
        if (form.password !== form.password_confirm) {
          setError("Passwords do not match.");
          setSubmitting(false);
          return;
        }
        const recaptcha_token = await getRecaptchaToken("register");
        const resp = await apiPost("/auth/register/", {
          ...form,
          recaptcha_token,
        });
        if (resp?.verification_sent) {
          setNotice("Account created — please check your email to verify your account before signing in.");
        } else {
          setNotice("Account created. You can sign in once your role is approved.");
        }
        setMode && setMode("login");
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (Array.isArray(err.body?.errors)) {
          setError(err.body.errors.join(" "));
        } else if (err.body?.verification_required) {
          setError("Please verify your email. A new verification link has been sent.");
        } else {
          setError(err.body?.error || err.message);
        }
      } else {
        setError(err.message || "Request failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="route-enter" style={{
      minHeight:"100vh",
      display:"grid",
      gridTemplateColumns:"1fr 1fr",
    }}>
      {/* Left — instrument panel, editorial */}
      <div style={{
        padding:"60px 60px",
        borderRight:"1px solid var(--line)",
        background:"var(--surface)",
        display:"flex", flexDirection:"column", justifyContent:"space-between",
        backgroundImage:"radial-gradient(circle at 30px 30px, var(--grid-dot) 1px, transparent 1px)",
        backgroundSize:"60px 60px"
      }}>
        <div className="row" style={{gap:12}}>
          <BrandMark size={28}/>
          <div style={{fontFamily:"var(--ff-h)", fontSize:22, letterSpacing:"-0.02em"}}>Venzyme<em style={{fontStyle:"italic", color:"var(--accent-hi)"}}>.LIFE</em></div>
        </div>
        <div>
          <div className="h-eyebrow" style={{marginBottom:20}}>Prospectus · {new Date().toLocaleDateString("en-US",{month:"2-digit",day:"2-digit",year:"2-digit"}).replace(/\//g,".")}</div>
          <h2 className="h-display" style={{fontSize:"var(--text-4xl)", lineHeight:1.05, letterSpacing:"-0.025em", maxWidth:480}}>
            Every deck you upload becomes a <em style={{fontStyle:"italic", color:"var(--accent-hi)"}}>structured record</em> — not a PDF in a folder.
          </h2>
          <p style={{color:"var(--text-mute)", marginTop:20, maxWidth:460, lineHeight:1.6}}>
            Extraction, scoring, market research, and notes — on one permanent record per company.
          </p>
        </div>
        <div className="as-of">SEC-aware · SOC 2 Type II · HIPAA available</div>
      </div>

      {/* Right — form */}
      <form onSubmit={submit} style={{padding:"60px 80px", display:"flex", flexDirection:"column", justifyContent:"center"}}>
        <div style={{maxWidth:420, width:"100%"}}>
          <div className="h-eyebrow" style={{marginBottom:12}}>{isLogin ? "Sign in" : "Create account"}</div>
          <h1 className="h-display" style={{fontSize:"var(--text-3xl)", marginBottom:28}}>
            {isLogin ? "Welcome back." : <>Start with a <em style={{fontStyle:"italic", color:"var(--accent-hi)"}}>deck</em>.</>}
          </h1>

          {notice && (
            <div style={{
              marginBottom:18, padding:"12px 14px",
              border:"1px solid var(--accent-hi)",
              borderRadius:4,
              background:"var(--accent-soft)",
              fontSize:13,
            }}>{notice}</div>
          )}
          {error && (
            <div style={{
              marginBottom:18, padding:"12px 14px",
              border:"1px solid var(--bad)",
              borderRadius:4,
              background:"color-mix(in srgb, var(--bad) 10%, transparent)",
              fontSize:13, color:"var(--bad)"
            }}>{error}</div>
          )}

          {!isLogin && (
            <>
              <Field label="Full name" value={form.user_name} onChange={set("user_name")} required autoComplete="name"/>
              <div style={{height:14}}/>
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:14}}>
                <SelectField
                  label="Account type"
                  value={form.account_type}
                  onChange={set("account_type")}
                  options={[
                    {v:"company", l:"Company (upload decks)"},
                    {v:"investor", l:"Investor (evaluate decks)"},
                    {v:"administrator", l:"Administrator"},
                  ]}
                />
                <Field label="Title" value={form.user_title} onChange={set("user_title")} autoComplete="organization-title"/>
              </div>
              {form.account_type === "company" && (
                <>
                  <div style={{height:14}}/>
                  <Field label="Company" value={form.company_name} onChange={set("company_name")} required/>
                </>
              )}
              <div style={{height:14}}/>
            </>
          )}
          <Field label="Work email" type="email" value={form.email} onChange={set("email")} required autoComplete="email"/>
          <div style={{height:14}}/>
          <Field label="Password" value={form.password} onChange={set("password")} type="password" required autoComplete={isLogin?"current-password":"new-password"}/>
          {!isLogin && (
            <>
              <div style={{height:14}}/>
              <Field label="Confirm password" value={form.password_confirm} onChange={set("password_confirm")} type="password" required autoComplete="new-password"/>
            </>
          )}

          {isLogin && (
            <div style={{marginTop:10, fontSize:13, color:"var(--text-mute)"}}>
              <a href="/password-reset-request/" style={{color:"var(--text-mute)"}}>Forgot password?</a>
            </div>
          )}

          <button type="submit" disabled={submitting} className="btn btn--primary" style={{width:"100%", padding:"14px", marginTop:24, justifyContent:"center", fontSize:15, opacity: submitting ? 0.6 : 1}}>
            {submitting ? "Working…" : (isLogin ? "Sign in" : "Create account")} <Ico n="arrow" size={14}/>
          </button>

          <div style={{marginTop:24, fontSize:13, color:"var(--text-mute)", textAlign:"center"}}>
            {isLogin ? <>No account yet? <a onClick={()=>setMode && setMode("register")} style={{color:"var(--accent-hi)", cursor:"pointer"}}>Request access →</a></>
                     : <>Already here? <a onClick={()=>setMode && setMode("login")} style={{color:"var(--accent-hi)", cursor:"pointer"}}>Sign in →</a></>}
          </div>

          <div style={{marginTop:40, paddingTop:20, borderTop:"1px solid var(--line)", fontSize:12, color:"var(--text-dim)", letterSpacing:".06em"}}>
            By continuing you agree to the Venzyme AUP. Enterprise SSO available on request.
          </div>
        </div>
      </form>
    </div>
  );
}

export function Field({ label, value, type = "text", onChange, required, autoComplete }) {
  return (
    <label style={{display:"block"}}>
      <div className="h-eyebrow" style={{marginBottom:6}}>{label}</div>
      <input
        type={type}
        value={value || ""}
        onChange={onChange}
        required={required}
        autoComplete={autoComplete}
        style={{
          width: "100%",
          boxSizing: "border-box",
          border: "1px solid var(--line-strong)",
          borderRadius: 4,
          padding: "10px 12px",
          background: "var(--surface)",
          fontSize: 14,
          color: "var(--text)",
          outline: "none",
        }}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label style={{display:"block"}}>
      <div className="h-eyebrow" style={{marginBottom:6}}>{label}</div>
      <select
        value={value}
        onChange={onChange}
        style={{
          width: "100%",
          boxSizing: "border-box",
          border: "1px solid var(--line-strong)",
          borderRadius: 4,
          padding: "10px 12px",
          background: "var(--surface)",
          fontSize: 14,
          color: "var(--text)",
          outline: "none",
        }}
      >
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

export function AuthPage() {
  const initialMode = (() => {
    try {
      const saved = localStorage.getItem("vz-auth-mode");
      if (saved === "register" || saved === "login") return saved;
    } catch { /* ignore */ }
    return "login";
  })();
  const [mode, setModeState] = useState(initialMode);
  const setMode = (m) => {
    setModeState(m);
    try { localStorage.setItem("vz-auth-mode", m); } catch { /* ignore */ }
  };
  return (
    <>
      <div style={{position:"fixed", top:20, right:20, zIndex:20}}>
        <div className="seg">
          <button className={mode==="login"?"on":""} onClick={()=>setMode("login")}>Sign in</button>
          <button className={mode==="register"?"on":""} onClick={()=>setMode("register")}>Register</button>
        </div>
      </div>
      <Auth mode={mode} setMode={setMode}/>
    </>
  );
}
